/* eslint-disable camelcase */
/**
 * @file PatientChangePassword.jsx
 * @author Fernando Ruano
 * @description Patient Change Password Page
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { Input, Form, Button, message } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
// import { SyncOutlined, UnlockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

// Components
import ButtonOutlined from '@nephro/components/ButtonOutlined';

// Helpers
import { validatePassword } from '@nephro/commons/helpers/utils';

// Assets
import unlock from '@nephro/assets/unlock.png';

// Styles
import styles from './styles.module.css';

function PatientChangePassword({ authActions }) {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [form] = Form.useForm();
  const { logIn, resetPassword } = authActions;

  React.useEffect(() => {}, []);

  const onNavigationBack = () => {
    history.goBack();
  };

  const afterChangePassword = (type) => {
    if (type === 'success') {
      onNavigationBack();
      message.success('¡Tu contraseña fue cambiada!');
    }
  };

  // Call user info services when logged
  const onLogin = (type, response, userNewPassword) => {
    if (type === 'success') {
      const data = {
        email: location?.state?.email,
        userNewPassword
      };
      resetPassword(data, afterChangePassword);
    } else {
      message.error('¡La contraseña actual es incorrecta!');
    }
  };

  // Function to get on submit form values
  const onFinish = (values) => {
    const { id_document, identification_type } = location.state;

    const data = {
      credentials: values.currentPassword,
      userName: id_document,
      isEmail: '0',
      identityProvider: 'OKTA_PROVIDER',
      documentType: identification_type.identification_type_id
    };
    logIn(data, (type, response) =>
      onLogin(type, response, values.newPassword)
    );
  };

  // After press continue button whit errors in form
  const onFinishFailed = () => message.error('¡Corrige los campos resaltados!');

  // Validate confirmation password
  const validateConfirmPas = (rule, value, callback) => {
    const { newPassword, confirmPassword } = form.getFieldsValue();
    if (newPassword !== confirmPassword)
      return callback('Las contraseñas no coinciden');
    return Promise.resolve();
  };

  /* eslint-disable no-template-curly-in-string */
  const validateMessages = {
    required: translate('commons.required'),
    types: {
      email: translate('commons.wrongEmail')
    }
  };
  /* eslint-enable no-template-curly-in-string */
  return (
    <div className="h-full flex flex-col px-5 py-4 md:w-1/2">
      <h3 className="text-xl font-bold text-left mb-7 text-baxterGray">
        {translate('registration.changePass')}
      </h3>
      <div className="w-full flex items-center justify-center">
        <img src={unlock} className="w-20" />
      </div>
      <h3 className="text-base text-left mt-7 mb-4 text-baxterGray">
        {translate('registration.newPass')}
      </h3>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        validateMessages={validateMessages}>
        <Form.Item
          label={translate('registration.currentPassword')}
          name="currentPassword"
          className="w-full"
          rules={[
            {
              required: true
            }
          ]}>
          <Input.Password
            autoComplete="off"
            className="h-10"
            placeholder={translate('login.password')}
          />
        </Form.Item>
        <Form.Item
          label={translate('registration.newPassword')}
          name="newPassword"
          className="w-full"
          rules={[
            {
              required: true
            },
            {
              validator: validatePassword
            }
          ]}>
          <Input.Password
            autoComplete="off"
            className="h-10"
            placeholder={translate('login.password')}
          />
        </Form.Item>

        <Form.Item
          label={translate('registration.confirmPass')}
          name="confirmPassword"
          className="w-full"
          rules={[
            {
              required: true
            },
            {
              validator: validateConfirmPas
            }
          ]}>
          <Input.Password
            autoComplete="off"
            className="h-10"
            placeholder={translate('login.password')}
          />
        </Form.Item>

        <div className="flex flex-row justify-between">
          <Form.Item className="w-full">
            <ButtonOutlined
              type="cyan"
              textButton={translate('commons.cancel')}
              classStyles={styles.cancelButton}
              onActionClick={onNavigationBack}
            />
          </Form.Item>
          <Form.Item className="w-full">
            <Button
              className={styles.stepTwoButton}
              type="primary"
              htmlType="submit">
              {translate('commons.confirm')}
            </Button>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
}

/**
 * Component definition of proptypes.
 */
PatientChangePassword.propTypes = {
  authActions: PropTypes.shape({
    logIn: PropTypes.func.isRequired,
    resetPassword: PropTypes.func.isRequired
  }).isRequired,
  patientActions: PropTypes.shape({
    updatePatient: PropTypes.func.isRequired,
    getPatientById: PropTypes.func.isRequired,
    getIdentificationTypesArray: PropTypes.func.isRequired
  }).isRequired
};

export default PatientChangePassword;
